import React, { useEffect, useState } from 'react';
import {
  CopyOutlined,
  ImportOutlined,
  RightOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Avatar } from 'antd';
import { translate } from '@portal/services/i18n';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '@portal/services/message';

import Briefcase from '@portal/assets/svg/profile/briefcase.svg?react';
import User from '@portal/assets/svg/profile/user.svg?react';
import Sms from '@portal/assets/svg/profile/sms.svg?react';

import { useDispatch } from 'react-redux';
import {
  AuthActions,
  CondominiumsActions,
  AccountActions,
} from '@portal/actions';
import { getMe } from '@portal/actions/user';

import * as StorageService from '~/services/storage';
import { maskCpfOrCnpj } from '@portal/services/masks';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { useImpersonationCheck } from '@portal/hooks/useImpersonationCheck';
import { getRouteStackPath } from '@portal/config/routes';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

interface ProfileProps {
  profile: string;
  name: string;
  mail: string;
  login: string;
  showLastCondominiums?: boolean;
  photoUrl: string;
}

const AdvancedProfile = (props: ProfileProps) => {
  const { mail, name, profile, photoUrl, showLastCondominiums = true } = props;
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const { isImpersonated } = useImpersonationCheck();

  const [condominiums, setCondominiums] = useState<models.Condominiums[]>([]);
  const [authUserStorage, setAuthUserStorage] = useState<models.User>();
  const [showSwitchAccountDropdown, setShowSwitchAccountDropdown] =
    useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [imageError, setImageError] = useState(false);

  const {
    user: { me: loggedUser },
    userImpersonation,
  } = useReduxState();

  const lastAccessData = userImpersonation?.lastAccess || [];

  useEffect(() => {
    const condominiumAccessData = StorageService.getItem('access-condominium');
    if (condominiumAccessData) {
      setCondominiums(JSON.parse(condominiumAccessData));
    }
  }, []);

  useEffect(() => {
    const authUserAccessData = StorageService.getItem('auth-user-admin');
    if (authUserAccessData) {
      setAuthUserStorage(JSON.parse(authUserAccessData));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  const OPTIONS = [
    {
      id: 1,
      icon: <User />,
      name: translate('COMPONENTS.PROFILE.MY_PROFILE'),
      navigation: '/minha-conta/informacoes-pessoais',
    },
    ...(lastAccessData && lastAccessData.length > 0
      ? [
          {
            id: 2,
            icon: <SwapOutlined />,
            name: translate('COMPONENTS.PROFILE.SWITCH_ACCOUNT'),
            navigation: 'switch-account',
            hasDropdown: true,
          },
        ]
      : []),
    {
      id: 3,
      icon: <ImportOutlined />,
      name: translate('COMPONENTS.PROFILE.LOGOUT'),
      navigation: 'logout',
    },
  ];

  const handleNavigation = (route: string) => {
    if (route.includes('logout')) {
      StorageService.removeItem('auth-user-admin');
      dispatch(AuthActions.logout());
      return;
    }

    if (route === 'switch-account') {
      return;
    }

    navigate(route);
  };

  const handleSwitchAccount = (userId: string) => {
    const mainAccountAccessToken = StorageService.getItem(
      StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN
    );
    const mainAccountRefreshToken = StorageService.getItem(
      StorageService.StorageItems.MAIN_ACCOUNT_REFRESH_TOKEN
    );

    if (mainAccountAccessToken && isImpersonated) {
      const targetAccount = lastAccessData.find(
        (item) => item.targetUserId === userId
      );
      if (targetAccount && !targetAccount.impersonatorUserId) {
        try {
          let actualAccessToken = mainAccountAccessToken;
          let actualRefreshToken = mainAccountRefreshToken;

          if (mainAccountAccessToken.startsWith('{')) {
            const tokenData = JSON.parse(mainAccountAccessToken);
            actualAccessToken = tokenData.accessToken;
            actualRefreshToken =
              tokenData.refreshToken || mainAccountRefreshToken;
          }

          const sessionTokenData = {
            accessToken: actualAccessToken,
            refreshToken: actualRefreshToken,
          };

          StorageService.setItem(
            StorageService.StorageItems.SESSION_TOKEN,
            JSON.stringify(sessionTokenData)
          );

          setHeader(
            StorageService.StorageItems.AUTHORIZATION,
            `Bearer ${actualAccessToken}`
          );

          // Clean up main account tokens to avoid loops
          StorageService.removeItem(
            StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN
          );
          StorageService.removeItem(
            StorageService.StorageItems.MAIN_ACCOUNT_REFRESH_TOKEN
          );

          setShowSwitchAccountDropdown(false);

          // Navigate to home with a slight delay
          setTimeout(() => {
            window.location.href = '/inicio';
          }, 100);
          return;
        } catch (error) {
          dispatch(AuthActions.logout());
        }
      }
    }

    dispatch(AccountActions.switchAccount(userId));
    setShowSwitchAccountDropdown(false);
  };

  const handleMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setShowSwitchAccountDropdown(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowSwitchAccountDropdown(false);
    }, 200);
    setDropdownTimeout(timeout);
  };

  const handleAccessCondominium = (documentNumber: string) => {
    dispatch(
      CondominiumsActions.AccountSwitch(documentNumber, loggedUser, (err) => {
        if (err) {
          MessageService.error('PAGES.CONDOMINIUMS.SWITCH_MESSAGE_ERROR');
        } else {
          dispatch(getMe());
          navigate('/inicio');
        }
      })
    );
  };

  return (
    <div className="profile">
      <div className="profile__box">
        <div className="profile__row">
          <div className="profile__avatar">
            {!imageError && photoUrl ? (
              <img
                src={photoUrl}
                className="profile__avatar_img"
                alt="profile_image"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="profile__avatar_fallback">
                <User />
              </div>
            )}
          </div>
          <div className="profile__info">
            <h4 className="profile__info__user">{name}</h4>
            <div className="profile__row">
              <Briefcase />
              <ThemedText variant="body">{profile}</ThemedText>
            </div>
          </div>
        </div>
        <div className="profile__line" />
        <div className="profile__login">
          <div className="profile__row">
            <Sms />
            <ThemedText variant="body">{mail}</ThemedText>
            <button
              className="profile__copy-email"
              onClick={() => {
                navigator.clipboard.writeText(mail);
                MessageService.success('COMPONENTS.PROFILE.EMAIL_COPIED');
              }}
              title={translate('COMPONENTS.PROFILE.COPY_EMAIL')}
            >
              <CopyOutlined />
            </button>
          </div>
        </div>
        {/* {renderAccountInfo()} */}
      </div>
      {showLastCondominiums && condominiums.length > 0 ? (
        <div className="profile__second_box">
          {condominiums.slice(-3).map((item) => (
            <div
              className="profile__condominiums"
              key={item.number}
              onClick={() => handleAccessCondominium(item.cnpj)}
            >
              <ThemedText variant="body" className="profile__text">
                {item.name}
              </ThemedText>
            </div>
          ))}
          <div
            className="profile__condominiums"
            onClick={() => handleNavigation('/condominios')}
          >
            <ThemedText variant="h5" className="profile__text">
              {translate('COMPONENTS.PROFILE.BUTTON')}
            </ThemedText>
          </div>
          {authUserStorage &&
          authUserStorage.account?.id !== loggedUser.account.id ? (
            <div
              className="profile__condominiums"
              onClick={() =>
                handleAccessCondominium(
                  authUserStorage.account.taxIdentifier ?? ''
                )
              }
            >
              <ThemedText variant="h5" className="profile__text">
                {translate('COMPONENTS.PROFILE.RETURN_ACCOUNT')}
              </ThemedText>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="profile__options">
        {OPTIONS.map((item) => (
          <div
            className="profile__options_list"
            key={item.id}
            onClick={() =>
              !item.hasDropdown && handleNavigation(item.navigation)
            }
            onMouseEnter={() => item.hasDropdown && handleMouseEnter()}
            onMouseLeave={() => item.hasDropdown && handleMouseLeave()}
          >
            {item.icon}
            <ThemedText variant="body" className="profile__text">
              {item.name}
            </ThemedText>
            <RightOutlined />
            {item.hasDropdown && showSwitchAccountDropdown ? (
              <div
                className="profile__switch_account_dropdown"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="profile__switch_account_header">
                  <ThemedText variant="h5">
                    {translate('COMPONENTS.PROFILE.RECENT_ACCOUNTS')}
                  </ThemedText>
                  <button
                    className="profile__switch_account_view_all"
                    onClick={(e) => {
                      e.stopPropagation();

                      const mainAccountAccessToken = StorageService.getItem(
                        StorageService.StorageItems.MAIN_ACCOUNT_ACCESS_TOKEN
                      );
                      const mainAccountRefreshToken = StorageService.getItem(
                        StorageService.StorageItems.MAIN_ACCOUNT_REFRESH_TOKEN
                      );

                      if (mainAccountAccessToken) {
                        let actualAccessToken = mainAccountAccessToken;
                        let actualRefreshToken = mainAccountRefreshToken;

                        if (mainAccountAccessToken.startsWith('{')) {
                          const tokenData = JSON.parse(mainAccountAccessToken);
                          actualAccessToken = tokenData.accessToken;
                          actualRefreshToken =
                            tokenData.refreshToken || mainAccountRefreshToken;
                        }

                        const sessionTokenData = {
                          accessToken: actualAccessToken,
                          refreshToken: actualRefreshToken,
                        };

                        StorageService.setItem(
                          StorageService.StorageItems.SESSION_TOKEN,
                          JSON.stringify(sessionTokenData)
                        );

                        setHeader(
                          StorageService.StorageItems.AUTHORIZATION,
                          `Bearer ${actualAccessToken}`
                        );
                      }

                      setShowSwitchAccountDropdown(false);

                      window.location.href = getRouteStackPath(
                        'PROFILE',
                        'ACCOUNT_SELECTION'
                      );
                    }}
                  >
                    {translate('COMPONENTS.PROFILE.BUTTON')}
                  </button>
                </div>
                {lastAccessData && lastAccessData.length > 0 ? (
                  lastAccessData.map((item) => (
                    <div
                      key={item.id}
                      className="profile__switch_account_item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSwitchAccount(item.targetUserId);
                      }}
                    >
                      <Avatar size={32}>
                        {item.targetUser.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <div className="profile__switch_account_info">
                        <ThemedText variant="h4">
                          {item.targetUser.name}
                        </ThemedText>
                        <ThemedText variant="body">
                          {item.targetUser.login}
                        </ThemedText>
                        <ThemedText>
                          {maskCpfOrCnpj(item.targetUser.taxIdentifier)}
                        </ThemedText>
                      </div>
                      <RightOutlined />
                    </div>
                  ))
                ) : (
                  <div className="profile__switch_account_empty">
                    <ThemedText variant="body">
                      {translate('COMPONENTS.PROFILE.NO_LAST_ACCESS')}
                    </ThemedText>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdvancedProfile;
